import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import axios from "axios";
import { ScrollMaskedLines } from "./MaskedText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch {
        return "";
    }
}

export default function GuestBook() {
    const [entries, setEntries] = useState([]);
    const [count, setCount] = useState(0);
    const [name, setName] = useState("");
    const [city, setCity] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/guestbook`);
                setEntries(r.data.entries || []);
                setCount(r.data.count || 0);
            } catch {
                // Wall stays empty; the form still works.
            }
        })();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !message.trim() || sending) return;
        setSending(true);
        setError("");
        try {
            const r = await axios.post(`${API}/guestbook`, {
                name: name.trim(),
                city: city.trim(),
                message: message.trim(),
            });
            setEntries(r.data.entries || []);
            setCount(r.data.count || 0);
            setSent(true);
            setName("");
            setCity("");
            setMessage("");
        } catch {
            setError(
                "Your letter could not be saved just now. Please try again in a moment."
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            data-testid="guestbook-section"
            id="letters"
            className="relative w-full bg-[color:var(--paper)] py-24 sm:py-32 border-t border-neutral-300"
        >
            <div className="max-w-[92rem] mx-auto px-6 sm:px-12 lg:px-16">
                {/* Section header */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 1 }}
                    className="grid grid-cols-12 pb-20 sm:pb-28 border-b border-neutral-300"
                >
                    <div className="col-span-12 sm:col-span-3 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                        Chapter IX — The Guest Book
                    </div>
                    <div className="col-span-12 sm:col-span-9 mt-4 sm:mt-0">
                        <ScrollMaskedLines
                            as="h2"
                            className="font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-tight text-neutral-950"
                            lines={["Write to Danielle."]}
                        />
                        <p className="mt-8 max-w-2xl font-serif italic text-lg sm:text-xl text-neutral-700 leading-relaxed">
                            A candle is a moment. A letter is a keepsake. Leave
                            her a few sentences — a memory, a prayer, a promise
                            — and it will rest here with the others, for her
                            mother to read.
                        </p>
                        <div
                            data-testid="guestbook-count"
                            className="mt-6 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500"
                        >
                            {count} {count === 1 ? "letter" : "letters"} to
                            Danielle
                        </div>
                    </div>
                </motion.div>

                <div className="pt-16 sm:pt-24 grid grid-cols-12 gap-10 sm:gap-16 items-start">
                    {/* Letter form */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 1 }}
                        className="col-span-12 lg:col-span-5"
                    >
                        {sent ? (
                            <div
                                data-testid="guestbook-success"
                                className="border-l border-neutral-400 pl-6 sm:pl-8"
                            >
                                <div className="font-sans text-[10px] uppercase tracking-[0.35em] text-amber-600">
                                    Your letter rests with her now
                                </div>
                                <p className="mt-6 font-serif italic text-2xl sm:text-3xl leading-snug text-neutral-950">
                                    Thank you. Every word written to her is one
                                    more proof that she is not forgotten.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setSent(false)}
                                    data-testid="guestbook-write-another"
                                    className="mt-8 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-900 ink-link hover:text-neutral-500 transition-colors"
                                >
                                    Write another letter →
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={submit}
                                data-testid="guestbook-form"
                                className="space-y-8"
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <label className="block">
                                        <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                            Your name
                                        </span>
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) =>
                                                setName(e.target.value)
                                            }
                                            maxLength={80}
                                            required
                                            data-testid="guestbook-name-input"
                                            placeholder="Name"
                                            className="mt-3 w-full bg-transparent border-b border-neutral-400 focus:border-neutral-900 outline-none py-3 font-serif text-lg text-neutral-950 placeholder:text-neutral-400 transition-colors"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                            City · optional
                                        </span>
                                        <input
                                            type="text"
                                            value={city}
                                            onChange={(e) =>
                                                setCity(e.target.value)
                                            }
                                            maxLength={80}
                                            data-testid="guestbook-city-input"
                                            placeholder="Where you&rsquo;re writing from"
                                            className="mt-3 w-full bg-transparent border-b border-neutral-400 focus:border-neutral-900 outline-none py-3 font-serif text-lg text-neutral-950 placeholder:text-neutral-400 transition-colors"
                                        />
                                    </label>
                                </div>
                                <label className="block">
                                    <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                        Your letter
                                    </span>
                                    <textarea
                                        value={message}
                                        onChange={(e) =>
                                            setMessage(e.target.value)
                                        }
                                        maxLength={1200}
                                        required
                                        rows={7}
                                        data-testid="guestbook-message-input"
                                        placeholder="Dear Danielle…"
                                        className="mt-3 w-full bg-transparent border border-neutral-400 focus:border-neutral-900 outline-none p-5 font-serif italic text-lg leading-relaxed text-neutral-950 placeholder:text-neutral-400 transition-colors resize-y"
                                    />
                                    <span className="mt-2 block text-right font-sans text-[10px] tracking-[0.2em] text-neutral-400 tabular">
                                        {message.length} / 1200
                                    </span>
                                </label>
                                {error && (
                                    <p
                                        data-testid="guestbook-error"
                                        className="font-serif italic text-base text-red-800"
                                    >
                                        {error}
                                    </p>
                                )}
                                <button
                                    type="submit"
                                    disabled={sending}
                                    data-testid="guestbook-submit-button"
                                    className="group relative inline-flex items-center gap-3 bg-neutral-900 text-neutral-50 px-8 py-5 min-h-[48px] font-sans text-xs uppercase tracking-[0.25em] font-bold overflow-hidden disabled:opacity-60"
                                >
                                    <span className="absolute inset-0 bg-amber-500 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.77,0,0.175,1)]" />
                                    <span className="relative group-hover:text-neutral-950 transition-colors duration-500">
                                        {sending
                                            ? "Placing your letter…"
                                            : "Leave the letter"}
                                    </span>
                                </button>
                            </form>
                        )}
                    </motion.div>

                    {/* Letters wall */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 1, delay: 0.15 }}
                        className="col-span-12 lg:col-span-6 lg:col-start-7"
                    >
                        <div className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-8">
                            Recent letters
                        </div>
                        {entries.length === 0 ? (
                            <p
                                data-testid="guestbook-empty"
                                className="font-serif italic text-lg sm:text-xl text-neutral-600 leading-relaxed max-w-lg"
                            >
                                The page is open and waiting. Yours can be the
                                first letter Danielle receives here.
                            </p>
                        ) : (
                            <ul
                                data-testid="guestbook-entries"
                                className="space-y-10"
                            >
                                {entries.map((entry, i) => (
                                    <motion.li
                                        key={entry.id}
                                        initial={{ opacity: 0, y: 16 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{
                                            duration: 0.8,
                                            delay: Math.min(i * 0.06, 0.5),
                                        }}
                                        data-testid={`guestbook-entry-${i}`}
                                        className="border-l border-neutral-300 pl-6 sm:pl-8"
                                    >
                                        <p className="font-serif italic text-lg sm:text-xl leading-relaxed text-neutral-800 whitespace-pre-line">
                                            &ldquo;{entry.message}&rdquo;
                                        </p>
                                        <div className="mt-4 font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                                            <span className="text-neutral-900">
                                                {entry.name}
                                            </span>
                                            {entry.city
                                                ? ` · ${entry.city}`
                                                : ""}
                                            {entry.created_at
                                                ? ` · ${formatDate(entry.created_at)}`
                                                : ""}
                                        </div>
                                    </motion.li>
                                ))}
                            </ul>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
